// src/components/suiviEnseignants/elementsSuivi.jsx
// ============================================================================
// Pièces partagées par la liste « Suivi des enseignants » et la page d'un
// enseignant : le choix de la semaine, l'état de l'espace enseignant, la
// carte de compteur, la barre d'avancement.
// ============================================================================
//
// Une seule écriture de chaque chose : sinon la liste dirait « Code non
// utilisé » là où la page de l'enseignant dirait autre chose pour le même état.
// ============================================================================

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { dateCourte, dateHeure, decalerJours, periode, pourcentage } from './formatSuivi';

// ----------------------------------------------------------------------------
// La semaine
// ----------------------------------------------------------------------------

/**
 * Semaine précédente / suivante, et retour à la semaine en cours.
 *
 * Le serveur choisit la semaine et en renvoie le lundi (`debut`) : on lui
 * demande « le lundi d'avant » ou « le lundi d'après », il recalcule. Le
 * retour à « cette semaine » n'envoie AUCUNE date — l'horloge du poste n'a pas
 * à dire quel jour on est. Il n'apparaît donc que si une semaine a été choisie.
 *
 * Les boutons restent actifs pendant un chargement : chaque écran ne garde que
 * la réponse de sa DERNIÈRE demande, cliquer vite de semaine en semaine est sûr.
 * Le pas part donc de la semaine DEMANDÉE (`choisie`, celle de l'adresse) et
 * non de celle affichée : deux clics avant la réponse reculent bien de deux
 * semaines, au lieu de redemander deux fois la même. `choisie` peut être
 * n'importe quel jour : à ± 7 jours, le serveur en recalcule le lundi.
 *
 * `desactive` (aucune semaine encore lue, ou refusée) ne touche que les
 * flèches : « Cette semaine » reste la sortie d'une adresse dont le serveur a
 * refusé la semaine (30 février), sans quoi l'écran restait bloqué sur son
 * erreur.
 */
export const ChoixSemaine = ({ semaine, choisie, onChoisir, desactive = false }) => {
  const debut = choisie || semaine?.debut;
  const texte = semaine ? `Semaine ${periode(semaine.debut, semaine.fin)}` : 'Semaine en cours';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-stretch overflow-hidden rounded-lg border border-gray-300 bg-white">
        <button
          type="button"
          aria-label="Semaine précédente"
          disabled={desactive || !debut}
          onClick={() => onChoisir(decalerJours(debut, -7))}
          className="flex h-9 w-9 items-center justify-center text-gray-600 transition hover:bg-gray-50 disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span aria-live="polite" className="flex h-9 min-w-[230px] items-center justify-center border-x border-gray-300 px-3 text-sm font-medium text-gray-800">
          {texte}
        </span>
        <button
          type="button"
          aria-label="Semaine suivante"
          disabled={desactive || !debut}
          onClick={() => onChoisir(decalerJours(debut, 7))}
          className="flex h-9 w-9 items-center justify-center text-gray-600 transition hover:bg-gray-50 disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {/* `size` explicite : le Button de l'interface n'a pas de taille par
          défaut, il serait rendu sans hauteur ni marge à côté du sélecteur. */}
      {choisie && (
        <Button type="button" variant="outline" size="default" onClick={() => onChoisir(null)}>
          Cette semaine
        </Button>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Onglets au clavier
// ----------------------------------------------------------------------------

/*
 * `clavierOnglets` a ete retiree le 10 octobre 2026.
 *
 * Elle reecrivait a la main le clavier ARIA d'une barre d'onglets (fleches,
 * Debut, Fin, ordre de tabulation, focus) pour les deux seules barres du
 * projet qui n'utilisaient pas les primitives `Tabs`. Les deux y sont passees,
 * et Radix fournit ce clavier. Si une barre d'onglets doit etre ecrite
 * ailleurs : passer par `src/components/ui/tabs.jsx`, pas par des boutons.
 */

// ----------------------------------------------------------------------------
// L'espace enseignant
// ----------------------------------------------------------------------------

// Mêmes teintes que l'onglet « Accès enseignant » du dossier de l'employé.
export const ETATS_ESPACE = {
  actif: ['Actif', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  suspendu: ['Suspendu', 'border-red-200 bg-red-50 text-red-700'],
  code_en_attente: ['Code non utilisé', 'border-amber-200 bg-amber-50 text-amber-800'],
  aucun: ['Aucun code', 'border-gray-200 bg-gray-100 text-gray-600']
};

/**
 * « Sans espace » : l'enseignant ne peut pas faire ses appels ni tenir son
 * programme aujourd'hui — pas de code, un code jamais saisi, ou un espace
 * suspendu. Un espace suspendu est compté : il existe, mais ne s'ouvre pas.
 */
export const estSansEspace = (espace) => (espace?.etat || 'aucun') !== 'actif';

/**
 * Badge de largeur fixe, pour la colonne « Espace » de la liste.
 * `relative` : le texte pour lecteur d'écran (`sr-only`, en position absolue)
 * reste dans la liste qui défile ; sans cela il se plaçait par rapport à la
 * page et l'élargissait, d'où un défilement horizontal sur téléphone.
 */
export const BadgeEspace = ({ espace }) => {
  const etat = espace?.etat || 'aucun';
  const [libelle, classes] = ETATS_ESPACE[etat] || ETATS_ESPACE.aucun;
  const vu = etat === 'actif' && espace?.derniere_connexion ? dateCourte(espace.derniere_connexion) : '';
  return (
    <span className={`relative inline-flex h-6 w-[132px] items-center justify-center whitespace-nowrap rounded-lg border text-xs font-medium ${classes}`}>
      {vu ? `${libelle} · ${vu}` : libelle}
      {vu && <span className="sr-only">, dernière connexion le {dateHeure(espace.derniere_connexion)}</span>}
    </span>
  );
};

/** La phrase complète, pour l'en-tête de la page d'un enseignant. */
export function phraseEspace(espace) {
  const etat = espace?.etat || 'aucun';
  if (etat === 'actif') {
    return espace?.derniere_connexion
      ? `Espace actif · dernière connexion le ${dateHeure(espace.derniere_connexion)}`
      : 'Espace actif · jamais connecté';
  }
  if (etat === 'suspendu') return 'Espace suspendu';
  if (etat === 'code_en_attente') return "Code d'accès remis, pas encore utilisé";
  return "Aucun code d'accès remis";
}

// ----------------------------------------------------------------------------
// Compteurs et avancement
// ----------------------------------------------------------------------------

/**
 * Une carte de compteur, au dessin de la vie scolaire.
 *
 * Decision de Daniel du 11 octobre 2026 : les cartes chiffrees de toute
 * l'application se ressemblent. Le modele est celui de `StatCard`
 * (VieScolairePage.jsx) — aplat en degrade, texte blanc, icone et libelle en
 * haut, valeur en gros au centre.
 *
 * `ton` ne colore plus le nombre, qui est blanc sur l'aplat : quand il vaut
 * « attention », c'est la NOTE qui prend un fond clair, pour que l'alerte
 * reste visible sans casser la couleur de la carte.
 *
 * ⚠️ Ce composant sert aussi a la fiche d'un enseignant et a la feuille de
 * notes : il change donc le dessin de ces ecrans-la aussi. C'est l'intention.
 * Les appels qui ne passent ni couleur ni icone gardent le bleu de la maison
 * et se passent d'icone.
 */
export const CarteCompteur = ({
  libelle, valeur, sur, note, ton = 'neutre', complement = null,
  icone: Icone = null, couleur = 'from-[#246990] to-[#3084b5]'
}) => (
  // ⚠️ L'aplat est sur la carte ELLE-MEME, pas sur un bloc interieur.
  //
  // Avec le degrade sur un enfant, l'enfant ne grandit qu'a son contenu tandis
  // que la grille etale toutes les cartes a la hauteur de la plus haute : le
  // bas de la carte restait blanc des que son texte etait plus court que celui
  // du voisin. `h-full` et la colonne flexible font que la couleur va
  // jusqu'au bord, quelle que soit la carte la plus bavarde de la rangee.
  <div className={`flex h-full flex-col overflow-hidden rounded-lg border-0 bg-gradient-to-r p-4 text-white shadow-md ${couleur}`}>
    <div className="mb-2 flex items-center gap-1.5">
      {Icone && <Icone size={16} className="shrink-0 text-white" aria-hidden="true" />}
      <span className="text-xs font-medium leading-tight">{libelle}</span>
      {complement}
    </div>

    <div className="flex flex-1 flex-col items-center justify-center">
      <span className="text-2xl font-bold">
        {valeur}
        {sur !== undefined && sur !== null && (
          <span className="text-sm font-medium text-white/80"> / {sur}</span>
        )}
      </span>
    </div>

    {note && (
      <div className={`mt-2 text-center text-xs ${ton === 'attention'
        ? 'rounded-lg bg-white/20 px-2 py-0.5 font-semibold text-white'
        : 'text-white/80'}`}
      >
        {note}
      </div>
    )}
  </div>
);

/** La barre d'avancement d'un programme, à la couleur de l'administration. */
export const BarreAvancement = ({ valeur, libelle }) => {
  const p = pourcentage(valeur);
  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={p}
      aria-label={libelle}
      className="block h-1.5 overflow-hidden rounded-full bg-gray-100"
    >
      <span className="block h-full rounded-full bg-[#246990]" style={{ width: `${p}%` }} />
    </span>
  );
};
